import type { UrlInfo, AttachmentInfo, EmailAnalysis } from '../models/ReportPayload';
import { HashUtil } from '../helpers/HashUtil';

/**
 * Analyzes email content to extract URLs, domains, and attachment information.
 * Ports MailItemExtensions.cs functionality.
 */
export class EmailAnalyzer {
  /**
   * Analyze email content and extract URLs, domains, and attachments.
   * @param htmlBody - HTML body of the email
   * @param attachments - Array of Office attachment objects
   * @returns Complete email analysis
   */
  static async analyze(
    htmlBody: string,
    attachments: Office.AttachmentDetails[]
  ): Promise<EmailAnalysis> {
    const urls = this.extractUrls(htmlBody);
    const domains = this.extractUniqueDomains(urls);
    const emailLinks = this.extractEmailLinks(htmlBody);
    const attachmentInfos = await this.analyzeAttachments(attachments);

    return {
      urls,
      domains,
      emailLinks,
      attachments: attachmentInfos,
      summaryText: this.buildSummaryText(urls, domains, emailLinks, attachmentInfos),
    };
  }

  /**
   * Extract all URLs from HTML content.
   * Uses DOMParser to safely parse HTML and extract href attributes.
   */
  static extractUrls(htmlBody: string): UrlInfo[] {
    const urls: UrlInfo[] = [];
    const seenUrls = new Set<string>();

    if (!htmlBody) {
      return urls;
    }

    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlBody, 'text/html');

      // Find all anchor elements with href
      const links = doc.querySelectorAll('a[href]');
      links.forEach((link) => {
        const href = link.getAttribute('href');
        if (href && !href.startsWith('mailto:') && !href.startsWith('#')) {
          const normalizedUrl = this.normalizeUrl(href);
          if (normalizedUrl && !seenUrls.has(normalizedUrl)) {
            seenUrls.add(normalizedUrl);
            urls.push({
              url: normalizedUrl,
              domain: this.extractDomain(normalizedUrl),
            });
          }
        }
      });

      // Also extract URLs from text content (might be obfuscated links)
      const textUrls = this.extractUrlsFromText(doc.body.textContent || '');
      textUrls.forEach((url) => {
        if (!seenUrls.has(url)) {
          seenUrls.add(url);
          urls.push({
            url,
            domain: this.extractDomain(url),
          });
        }
      });
    } catch (error) {
      console.warn('Error parsing HTML for URLs:', error);
    }

    return urls;
  }

  /**
   * Extract URLs from plain text using regex.
   */
  private static extractUrlsFromText(text: string): string[] {
    const urlPattern = /https?:\/\/[^\s<>"']+/gi;
    const matches = text.match(urlPattern) || [];
    return matches.map((url) => this.normalizeUrl(url)).filter((url): url is string => !!url);
  }

  /**
   * Normalize a URL (remove tracking parameters, decode, etc.)
   */
  private static normalizeUrl(url: string): string | null {
    try {
      // Clean up URL
      let cleanUrl = url.trim();

      // Remove common trailing punctuation that might be accidentally included
      cleanUrl = cleanUrl.replace(/[.,;:!?)>\]]+$/, '');

      // Try to parse as URL to validate
      const parsed = new URL(cleanUrl);

      // Only return http/https URLs
      if (parsed.protocol === 'http:' || parsed.protocol === 'https:') {
        return parsed.href;
      }
    } catch {
      // Invalid URL
    }
    return null;
  }

  /**
   * Extract domain from a URL.
   */
  static extractDomain(url: string): string {
    try {
      const parsed = new URL(url);
      return parsed.hostname.toLowerCase();
    } catch {
      return '';
    }
  }

  /**
   * Extract unique domains from URL list.
   */
  static extractUniqueDomains(urls: UrlInfo[]): string[] {
    const domains = new Set<string>();
    urls.forEach((urlInfo) => {
      if (urlInfo.domain) {
        domains.add(urlInfo.domain);
      }
    });
    return Array.from(domains).sort();
  }

  /**
   * Extract email addresses from mailto: links.
   */
  static extractEmailLinks(htmlBody: string): string[] {
    const emails: string[] = [];
    const seenEmails = new Set<string>();

    if (!htmlBody) {
      return emails;
    }

    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlBody, 'text/html');

      const links = doc.querySelectorAll('a[href^="mailto:"]');
      links.forEach((link) => {
        const href = link.getAttribute('href');
        if (href) {
          const email = href.replace('mailto:', '').split('?')[0].trim().toLowerCase();
          if (email && !seenEmails.has(email)) {
            seenEmails.add(email);
            emails.push(email);
          }
        }
      });
    } catch (error) {
      console.warn('Error extracting email links:', error);
    }

    return emails;
  }

  /**
   * Analyze attachments and compute hashes.
   * Note: In Office.js, we can only get attachment content through getAttachmentContentAsync
   * which requires special handling.
   */
  static async analyzeAttachments(
    attachments: Office.AttachmentDetails[]
  ): Promise<AttachmentInfo[]> {
    const results: AttachmentInfo[] = [];

    for (const attachment of attachments) {
      // We can get basic info without content
      // Hash computation requires reading the actual content
      const info: AttachmentInfo = {
        fileName: attachment.name,
        size: attachment.size,
        md5Hash: '',
        sha256Hash: '',
        contentType: attachment.contentType || 'application/octet-stream',
      };

      results.push(info);
    }

    return results;
  }

  /**
   * Analyze a single attachment with content.
   * Call this when you have access to the attachment bytes.
   */
  static async analyzeAttachmentContent(
    fileName: string,
    content: ArrayBuffer,
    contentType: string
  ): Promise<AttachmentInfo> {
    const hashes = await HashUtil.calculateHashes(content);

    return {
      fileName,
      size: content.byteLength,
      md5Hash: hashes.md5,
      sha256Hash: hashes.sha256,
      contentType,
    };
  }

  /**
   * Build summary text for the report body.
   * Matches the VSTO plugin's format.
   */
  static buildSummaryText(
    urls: UrlInfo[],
    domains: string[],
    emailLinks: string[],
    attachments: AttachmentInfo[]
  ): string {
    const lines: string[] = [];

    lines.push('=== URLs and Attachments Analysis ===');
    lines.push('');

    // URLs section
    lines.push(`URLs Found: ${urls.length}`);
    if (urls.length > 0) {
      urls.forEach((u, i) => {
        lines.push(`  [${i + 1}] ${u.url}`);
      });
    }
    lines.push('');

    // Domains section
    lines.push(`Unique Domains: ${domains.length}`);
    if (domains.length > 0) {
      domains.forEach((d) => {
        lines.push(`  - ${d}`);
      });
    }
    lines.push('');

    // Email links section
    if (emailLinks.length > 0) {
      lines.push(`Email Links: ${emailLinks.length}`);
      emailLinks.forEach((e) => {
        lines.push(`  - ${e}`);
      });
      lines.push('');
    }

    // Attachments section
    lines.push(`Attachments: ${attachments.length}`);
    if (attachments.length > 0) {
      attachments.forEach((a) => {
        lines.push(`  File: ${a.fileName}`);
        lines.push(`    Size: ${this.formatFileSize(a.size)}`);
        lines.push(`    Type: ${a.contentType}`);
        if (a.md5Hash) {
          lines.push(`    MD5: ${a.md5Hash}`);
        }
        if (a.sha256Hash) {
          lines.push(`    SHA256: ${a.sha256Hash}`);
        }
      });
    }

    return lines.join('\n');
  }

  /**
   * Format file size for display.
   */
  private static formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }
}
